import type { FeatureFlagKey } from '@app/types'

export interface AppLinkTypes {
  label: string
  path: string
  absolutePath?: boolean
  icon: string
  requiresAuth?: boolean
  featureFlag?: FeatureFlagKey
  featureFlagReverse?: boolean
  isBeta?: boolean
  showInMenu?: boolean
  parent?: AppLinkKey
  order?: number
}

export type AppLinkKey =
  | 'homepage'
  | 'risk'
  | 'riskExplorer'
  | 'riskWorkspaces'
  | 'riskWorkspacesCreate'
  | 'riskWorkspacesEdit'
  | 'workspaces'
  | 'workspacesCreate'
  | 'access'
  | 'forum'
  | 'adaptation'
  | 'property'
  | 'guidePage'
  | 'guideGetStarted'
  | 'guideFundamentals'
  | 'guideMethods'
  | 'guideInsights'
  | 'guideTutorials'
  | 'guideDemos'
  | 'guideGlossary'
  | 'onboarding'
  | 'newLanding'
  | 'homeWelcome'
  | 'homeFeatures'
  | 'homeUseCases'
  | 'homeAdaptationCycle'
  | 'homeProjectManagement'
  | 'homeProcess'
  | 'homeContact'
  | 'homeAboutUs'
  | 'live'
  | 'manageUsers'
  | 'website'
  | 'homeData'
  | 'homeDataDetail'
  | 'homeDataUpload'
  | 'smokeShadCn'
  | 'rexLive'
  | 'riskOverview'
  | 'riskSummary'
  | 'riskPrioritisation'
  | 'riskStories'
  | 'riskProfile'

export const appLink: Record<AppLinkKey, AppLinkTypes> = {
  website: {
    label: 'Website',
    path: '/',
    icon: 'WebsiteIcon',
    requiresAuth: false,
  },

  // ─── Top-level menu items ───────────────────────────────────────────────────

  homepage: {
    label: 'Home',
    path: '/app/home',
    icon: 'HomeIcon',
    showInMenu: true,
    requiresAuth: false,
    order: 1,
  },
  guidePage: {
    label: 'Guide',
    path: '/app/guide',
    icon: 'GuideIcon',
    featureFlag: 'guidePage',
    showInMenu: true,
    requiresAuth: true,
    order: 2,
  },
  risk: {
    label: 'Risk',
    path: '/app/risk',
    icon: 'RiskIcon',
    featureFlag: 'riskModule',
    showInMenu: true,
    requiresAuth: true,
    order: 3,
  },
  rexLive: {
    label: 'Live',
    path: '/app/live',
    icon: 'RexLiveIcon',
    featureFlag: 'rexLive',
    isBeta: true,
    showInMenu: true,
    requiresAuth: true,
    order: 4,
  },
  access: {
    label: 'Access',
    path: '/app/access',
    icon: 'AccessIcon',
    featureFlag: 'hideAccessModule',
    featureFlagReverse: true,
    showInMenu: true,
    requiresAuth: false,
    order: 5,
  },
  property: {
    label: 'Property',
    path: '/app/property/search',
    icon: 'PropertyIcon',
    featureFlag: 'propertyPage',
    isBeta: true,
    showInMenu: true,
    requiresAuth: true,
    order: 6,
  },
  forum: {
    label: 'Forum',
    path: 'https://forum.resilience-explorer.com/',
    absolutePath: true,
    icon: 'ForumIcon',
    featureFlag: 'forumLink',
    showInMenu: true,
    requiresAuth: true,
    order: 7,
  },

  // ─── Home sub-items ─────────────────────────────────────────────────────────

  homeWelcome: {
    label: 'Welcome',
    path: '/app/home/welcome',
    icon: 'WavingHand',
    parent: 'homepage',
    requiresAuth: false,
    showInMenu: true,
  },
  homeFeatures: {
    label: 'New features',
    path: '/app/home/features',
    icon: 'WavingHand',
    parent: 'homepage',
    requiresAuth: false,
    showInMenu: true,
  },
  homeUseCases: {
    label: 'Use cases',
    path: '/app/home/use-cases',
    icon: 'WavingHand',
    parent: 'homepage',
    requiresAuth: false,
    showInMenu: true,
  },
  homeData: {
    label: 'Data',
    path: '/app/home/data',
    icon: 'DataIcon',
    parent: 'homepage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'dataSubmodule',
  },
  homeDataUpload: {
    label: 'Data upload',
    path: '/app/home/data-upload',
    icon: 'DataIcon',
    parent: 'homepage',
    isBeta: true,
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'dataUpload',
  },
  homeAdaptationCycle: {
    label: 'Adaptation Cycle',
    path: '/app/home/adaptation-cycle',
    icon: 'WavingHand',
    parent: 'homepage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'adaptationCycle',
  },
  homeProjectManagement: {
    label: 'Project Management',
    path: '/app/home/project-management',
    icon: 'FolderKanban',
    parent: 'homepage',
    requiresAuth: true,
    showInMenu: false,
  },
  homeProcess: {
    label: 'The Process',
    path: '/app/home/process',
    icon: 'GitBranch',
    parent: 'homepage',
    requiresAuth: false,
    showInMenu: false,
  },
  homeContact: {
    label: 'Contacts',
    path: '/app/home/contact',
    icon: 'Users',
    parent: 'homepage',
    requiresAuth: true,
    showInMenu: false,
  },
  homeAboutUs: {
    label: 'About Us',
    path: '/app/home/about-us',
    icon: 'Info',
    parent: 'homepage',
    requiresAuth: false,
    showInMenu: false,
  },
  homeDataDetail: {
    label: 'Risk Data Detail',
    path: '/app/home/data/detail/:id',
    icon: 'DataIcon',
    parent: 'homeData',
    requiresAuth: true,
    showInMenu: false,
    featureFlag: 'dataSubmodule',
  },

  // ─── Guide sub-items ────────────────────────────────────────────────────────

  guideGetStarted: {
    label: 'Get Started',
    path: '/app/guide/get-started',
    icon: 'GuideIcon',
    parent: 'guidePage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'guidePage',
  },
  guideFundamentals: {
    label: 'Fundamentals',
    path: '/app/guide/fundamentals',
    icon: 'GuideIcon',
    parent: 'guidePage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'guidePage',
  },
  guideMethods: {
    label: 'Methods',
    path: '/app/guide/methods',
    icon: 'GuideIcon',
    parent: 'guidePage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'guidePage',
  },
  guideInsights: {
    label: 'Insights',
    path: '/app/guide/insights',
    icon: 'GuideIcon',
    parent: 'guidePage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'guidePage',
  },
  guideTutorials: {
    label: 'Tutorials',
    path: '/app/guide/tutorials',
    icon: 'GuideIcon',
    parent: 'guidePage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'guidePage',
  },
  guideDemos: {
    label: 'Demos',
    path: '/app/guide/demos',
    icon: 'GuideIcon',
    parent: 'guidePage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'guidePage',
  },
  guideGlossary: {
    label: 'Glossary',
    path: '/app/guide/glossary',
    icon: 'GuideIcon',
    parent: 'guidePage',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'guidePage',
  },

  // ─── Risk sub-items ─────────────────────────────────────────────────────────
  riskOverview: {
    label: 'Overview',
    path: '/app/risk/overview',
    icon: 'ExplorerIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'riskModule',
  },
  riskProfile: {
    label: 'Profile',
    path: '/app/risk/profile',
    icon: 'RiskIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'riskProfile',
  },
  riskExplorer: {
    label: 'Risk',
    path: '/app/risk/explorer',
    icon: 'RiskIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'riskModule',
  },
  riskSummary: {
    label: 'Summary',
    path: '/app/risk/summary',
    icon: 'SummaryIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'riskModule',
  },
  riskPrioritisation: {
    label: 'Prioritisation',
    path: '/app/risk/prioritisation',
    icon: 'PrioritisationIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'riskModule',
  },
  riskStories: {
    label: 'Stories',
    path: '/app/risk/stories',
    icon: 'StoriesIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'riskModule',
  },

  // Workspaces
  riskWorkspaces: {
    label: 'Workspaces',
    path: '/app/risk/explorer/workspaces/saved',
    icon: 'WorkspaceIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'riskModule',
  },
  riskWorkspacesCreate: {
    label: 'Create Workspace',
    path: '/app/risk/explorer/workspaces/create',
    icon: 'CreateIcon',
    parent: 'riskWorkspaces',
    requiresAuth: true,
    showInMenu: false,
  },
  riskWorkspacesEdit: {
    label: 'Edit Workspace',
    path: '/app/risk/explorer/workspaces/:workspaceId/edit',
    icon: 'EditIcon',
    parent: 'riskWorkspaces',
    requiresAuth: true,
    showInMenu: false,
  },

  // Workspaces V2
  workspaces: {
    label: 'Workspaces v2',
    path: '/app/risk/workspaces',
    icon: 'WorkspaceIcon',
    parent: 'risk',
    requiresAuth: true,
    showInMenu: true,
    featureFlag: 'workspacesV2',
  },
  workspacesCreate: {
    label: 'Create Workspace',
    path: '/app/risk/workspaces/create',
    icon: 'CreateIcon',
    parent: 'riskWorkspaces',
    requiresAuth: true,
    showInMenu: false,
  },

  // ─── Misc ───────────────────────────────────────────────────────────────────

  live: {
    label: 'Live',
    path: '/app/live',
    icon: 's3://live',
    featureFlag: 'livePage',
    showInMenu: false,
    requiresAuth: false,
  },
  adaptation: {
    label: 'Adaptation',
    path: '/app/adaptation',
    icon: 'ArrowLeftRight',
    showInMenu: false,
    requiresAuth: true,
  },
  onboarding: {
    label: 'Getting Started',
    path: '/app/onboarding',
    icon: 's3://waving',
    showInMenu: false,
    requiresAuth: true,
  },
  newLanding: {
    label: 'New Experience',
    path: '/app/new-landing',
    icon: 'NewReleases',
    featureFlag: 'newLandingPage',
    requiresAuth: true,
  },
  manageUsers: {
    label: 'Manage Users',
    path: '/app/admin/manage-users',
    icon: 'Users',
    requiresAuth: true,
  },
  smokeShadCn: {
    label: 'Smoke Test ShadCN',
    path: '/app/smoke',
    icon: 'ShadCNIcon',
    featureFlag: 'shadcnUi',
    isBeta: true,
    showInMenu: true,
    requiresAuth: true,
  },
} satisfies Record<keyof typeof appLink, AppLinkTypes>
